import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getStringWithoutHash } from 'utilitys/helpers/general';

import useCustomPageSubTitle from 'hooks/useCustomPageSubTitle';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';
import useTableCurrentState from 'hooks/useTableCurrentState';

import { useConfigurationStore } from 'store/configurationsStore';

import { useMutationSetWidgetColumns } from 'api/mutations/setWidgetColumns.mutation';
import { useQueryRecomendationTableData } from 'api/queries/recommendationTable.query';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { CustomTableColumnsModal } from 'components/common/Modals/CustomTableColumns/customTableColumnsModal';
import NoData from 'components/common/NoData/noData';
import MainTable from 'components/common/Tables/MainTable/MainTable';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import classes from 'components/Dashboards/Project/Components/VersionCompare/style/Table.module.scss';
import {
    TRecommendationTabs,
    generateRecommendationsTabs,
} from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Tabs } from 'components/DesignSystem/Tabs';
import { Filters } from 'components/widgets/Recommendations/Filters';
import { columns, initialState } from 'components/widgets/Recommendations/recommendations.utils';

export type TSeverity = 'High' | 'Medium' | 'Low';

interface IChosenFilter {
    severity: ISelectOption<TSeverity>[];
}

export const RecommendationsTable = ({
    widgetId,
    filter,
    externalComponents,
    editNarrative,
    projectId,
    contractId,
    latestVersionId,
    compareVersionId,
    setSubTitleForExport,
}: IComponentProps) => {
    const { componentKey, routeId: id, endpoints, name: configTitle } = useConfigurationStore().topRecommendations;
    const { data, isLoading, isFetching } = useQueryRecomendationTableData(
        { projectId, contractId, latestVersionId, compareVersionId },
        endpoints.getTopRecommendationChanges,
    );
    const tableRef = useRef(null);
    const [chosenFilter, setChosenFilter] = useState<IChosenFilter>({ severity: [] });
    const [search, setSearch] = useState<string>('');
    const [modalColumnsOpen, setModalColumnsOpen] = useState<boolean>(false);

    const filters = useMemo(
        () =>
            widgetId
                ? filter?.filters
                : getFiltersData({
                      ...chosenFilter,
                      severity: chosenFilter.severity.map((item) => item.value),
                  }),

        [filter, chosenFilter],
    );

    const handleStatus = useCallback((value, key) => {
        setChosenFilter((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handleSearch = useCallback((e) => {
        setSearch(e.target.value);
    }, []);

    const handleClearSearch = useCallback(() => {
        setSearch('');
        setChosenFilter((prev) => ({ ...prev, Recommendation: [] }));
    }, []);

    const handleOnSearch = useCallback(() => {
        setChosenFilter((prev) => ({ ...prev, Recommendation: [search] }));
    }, [search]);

    const [activeTab, setActiveTab] = useState<TRecommendationTabs>('all');

    const tabsArrData = generateRecommendationsTabs({ data });

    const activeData = useMemo(() => {
        if (data) {
            const currentData =
                activeTab === 'all'
                    ? data.recommendationResponse
                    : data.recommendationResponse.filter((item) => item.category.name === activeTab);
            if (currentData.length > 0 && filters) {
                const filterRecommendation = filters.find((filter) => filter.name === 'Recommendation');
                const filterSeverity = filters.find((filter) => filter.name === 'severity');

                return currentData
                    .filter((item) => {
                        if (filterRecommendation) {
                            const filter = filterRecommendation?.data?.map((item) => item.toLowerCase()) as string[];
                            const value = item.title as string;

                            return value.toLowerCase().includes(filter[0]);
                        }
                        return item;
                    })
                    .filter((item) => {
                        if (filterSeverity) {
                            const value = item.severity as string;
                            const filter = filterSeverity.data as string[];
                            return filter?.includes(value);
                        }
                        return item;
                    });
            }
            return currentData;
        }
        return [];
    }, [data, activeTab, filters]);

    const route = `${getStringWithoutHash(window.location.href)}#${id}`;
    const subTitle = useCustomPageSubTitle({ projectId, contractId, latestVersionId });
    const updatedInitialState = useTableCurrentState({ initialState, componentKey, widgetId });
    const { handleSnackBar } = useCustomSnackBar();
    const { mutate: mutateSetWidgetColumns } = useMutationSetWidgetColumns();
    const { generateFilters: formattedFilter } = useGlobalFiltersHook({ filter, latestVersionId });
    const setWidgetColumns = (widgetId: string) => {
        if (tableRef.current) {
            const { hiddenColumns, columnOrder } = tableRef.current.state;
            mutateSetWidgetColumns(
                { columns: { hiddenColumns, columnOrder }, widgetId },
                {
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
    };

    useEffect(() => {
        setSubTitleForExport && setSubTitleForExport({ widgetId, subTitle, title: configTitle });
    }, [widgetId, subTitle]);

    return (
        <WidgetWithTitle
            title={<WidgetTitle title={configTitle} />}
            id={id}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId && formattedFilter.length > 0 ? (
                    <PinboardWidgetPills key={'widgetFilters'} filters={formattedFilter} />
                ) : !widgetId ? (
                    <Filters
                        key={'filters'}
                        handleStatus={handleStatus}
                        chosenFilter={chosenFilter}
                        search={search}
                        handleSearch={handleSearch}
                        handleClearSearch={handleClearSearch}
                        handleOnSearch={handleOnSearch}
                    />
                ) : null,
            ]}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={configTitle}
                    projectId={projectId}
                    contractId={contractId}
                    elementId={id}
                    widgetId={widgetId}
                    route={route}
                    filters={filters}
                    callBack={setWidgetColumns}
                    shouldHide={true}
                />,
                <ExportDropdown title={configTitle} key={'export'} subTitle={subTitle} />,
                tableRef?.current && (
                    <ColumnsManager
                        onOpenModal={() => setModalColumnsOpen(true)}
                        onHideModal={() => setModalColumnsOpen(false)}
                        showModal={modalColumnsOpen}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        tableInstance={tableRef.current}
                        initialState={initialState}
                        columns={columns}
                    />
                ),
                <TooltipIcon key={'info'} tooltip={data?.info} />,
                <ThreeDotsMenu
                    widgetId={widgetId}
                    componentKey={componentKey}
                    shareTitle={configTitle}
                    shareLink={route}
                />,
            ]}
        >
            <div className={classes.tableWrapperContainer}>
                {tabsArrData.length > 0 && (
                    <Tabs
                        tabs={tabsArrData}
                        value={activeTab}
                        onChange={(value) => setActiveTab(value as TRecommendationTabs)}
                    />
                )}

                {updatedInitialState && (
                    <MainTable
                        getInstanceCallback={(instance) => (tableRef.current = instance)}
                        initialState={updatedInitialState}
                        data={activeData}
                        columns={columns}
                        pageSize={1000}
                        uniqueKey={'id'}
                        isLoading={isLoading || isFetching}
                        noData={<NoData>No Changes To Display</NoData>}
                    />
                )}

                {tableRef?.current && (
                    <CustomTableColumnsModal
                        onHideModal={() => setModalColumnsOpen(false)}
                        showModal={modalColumnsOpen}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        tableInstance={tableRef.current}
                        initialState={initialState}
                        columns={columns}
                    />
                )}
            </div>
        </WidgetWithTitle>
    );
};
